<template>
    <v-container grid-list-xs>
        <v-row class="mt-5">
            <v-col>
                <v-card>
                    <Form :messages="messages" :validations="validations" @submit="submit">
                        <v-card-text>
                            <PageHeader>
                                <template #header>
                                    <h4 class="mb-1">{{ $t("correct-errors.header") }}</h4>
                                </template>
                                <template #subheader>
                                    {{ $t("correct-errors.subheader") }}
                                </template>
                            </PageHeader>

                            <div class="pt-sm-5">
                                <v-subheader class="input-title">{{
                                    $t("additionalInfo.payment-frequency")
                                }}</v-subheader>
                                <p>{{ $t("additionalInfo.payment-frequency-description") }}</p>
                                <v-row dense>
                                    <v-col>
                                        <AppField
                                            v-if="fields['primaryIncomePayFrequency']"
                                            :application="application"
                                            :field="fields['primaryIncomePayFrequency']"
                                            type="select"
                                            label-code="field.payFrequency"
                                            :form-settings="formSettings"
                                            append-icon="$chevronDown"
                                        >
                                        </AppField>
                                    </v-col>
                                </v-row>
                                <v-row dense>
                                    <v-col cols="12" sm="6">
                                        <AppField
                                            v-if="fields['primaryIncomePreviousPayDate']"
                                            :application="application"
                                            :field="fields['primaryIncomePreviousPayDate']"
                                            label-code="field.previousPayDate"
                                            type="date"
                                            :form-settings="formSettings"
                                            :max="today"
                                        />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <AppField
                                            v-if="fields['primaryIncomeNextPayDate']"
                                            :application="application"
                                            :field="fields['primaryIncomeNextPayDate']"
                                            type="date"
                                            label-code="field.nextPayDate"
                                            :form-settings="formSettings"
                                            :min="today"
                                        />
                                    </v-col>
                                </v-row>
                                <v-row dense>
                                    <v-col cols="12" sm="6">
                                        <AppField
                                            v-if="fields['primaryEmail']"
                                            ref="primaryEmailField"
                                            :application="application"
                                            :field="fields['primaryEmail']"
                                            label-code="field.email"
                                            type="email"
                                            :form-settings="formSettings"
                                            @input="removeEmailErrorBanner"
                                        />
                                    </v-col>
                                </v-row>
                            </div>
                        </v-card-text>

                        <v-card-actions class="pb-12">
                            <v-row justify="space-between">
                                <v-col class="text-right">
                                    <SubmitButton class="secondary" rounded>
                                        {{ $t("action.submit") }}
                                    </SubmitButton>
                                </v-col>
                            </v-row>
                        </v-card-actions>
                    </Form>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import { applicationState, sessionState } from "@/state"
    import Providers from "@/model/provider/Providers"
    import { groupBy, indexBy } from "@/util/array"
    import { mapState } from "vuex"
    import { buildError, payDateNotValidForFrequency } from "@/util/validation"
    import pick from "lodash/pick"
    import { googleAnalyticsService } from "@/service"
    import GoogleAnalyticEvents from "@/model/google/GoogleAnalyticEvents"

    export default {
        name: "AffCorrectErrorsView",
        data() {
            return {
                currentProviderInfo: Providers[sessionState.dynamicProviderKey.providerKey],
                today: new Date(),
                formSettings: {
                    readOnly: false,
                },
                messages: {
                    en: {
                        rule: {
                            payDateNotValidForFrequency: "Next pay date not valid for pay frequency",
                            emailNotValid: "Blacklisted Email Domain",
                        },
                    },
                },
                validations: {
                    primaryIncomeNextPayDate: {
                        payDateNotValidForFrequency: this.calcPayDateNotValidForFrequency,
                    },
                    primaryEmail: {
                        emailNotValid: this.checkEmailInvalid,
                    },
                },
                editableFieldIds: [],
            }
        },

        computed: {
            ...mapState("field", {
                fields: state => indexBy(state.fields, "id"),
                fieldsByPath: state => indexBy(state.fields, "path"),
            }),

            ...mapState("field", ["fieldGroupRules"]),

            ...mapState("application", {
                application: state => state.data,
                applyMode: state => state.applyMode,
            }),

            ...mapState("validation", {
                fieldErrors: state => state.data.validationErrorResponse.fieldErrors,
            }),

            formErrors() {
                return indexBy(
                    this.fieldErrors.map(error => {
                        let key = error.message.replace(/^rule\./, "")
                        return buildError(key, null, "Label", error.params)
                    }),
                    "id"
                )
            },

            invalidFieldIds() {
                return Object.keys(groupBy(this.fieldErrors, "field"))
            },

            invalidFields() {
                return pick(this.fields, this.invalidFieldIds)
            },

            editableFields() {
                // There are currently some issues with showing only some of the fields in Applicant.vue, so just return all the fields for now
                // return pick(this.fields, this.editableFieldIds)
                return this.fields
            },

            isDisabled() {
                return (
                    !this.application.primary.income.payFrequency ||
                    !this.application.primary.income.previousPayDate ||
                    !this.application.primary.income.nextPayDate ||
                    !this.application.primary.mobilePhone ||
                    !this.application.primary.email
                )
            },
        },

        mounted() {
            this.$refs.primaryEmailField.validate()
        },

        methods: {
            calcPayDateNotValidForFrequency(value, parentVM) {
                return payDateNotValidForFrequency(value, parentVM)
            },

            checkEmailInvalid() {
                let isValid = true

                this.fieldErrors.forEach(function (fieldError) {
                    if (fieldError.message === "blacklistedEmail") {
                        isValid = false
                    }
                })

                return isValid
            },

            submit() {
                applicationState.application = this.application
                googleAnalyticsService.buttonEvent(
                    GoogleAnalyticEvents.ACCEPT_OFFER_SUBMITTED,
                    "Aff Accept Offer Submitted Error Corrections"
                )
                this.$router.push(`/providers/${sessionState.providerKey}/acceptOfferSubmit`)
            },

            removeEmailErrorBanner() {
                this.validations.primaryEmail.emailNotValid = false
            },
        },
    }
</script>
